/**
 * Password validation utility for user-friendly password requirements
 *
 * Requirements:
 * - Minimum 8 characters
 * - At least one letter (uppercase or lowercase)
 * - At least one number OR special character
 *
 * This is much more lenient than the previous policy which required:
 * uppercase AND lowercase AND number AND special character.
 *
 * Now most passwords from password managers like 1Password, LastPass, etc. will work.
 */

export interface PasswordValidationResult {
  checks: {
    minLength: boolean;
    hasLetter: boolean;
    hasNumberOrSymbol: boolean;
  };
  isValid: boolean;
}

/**
 * Validates a password against user-friendly requirements
 * @param password - The password to validate
 * @returns Validation result with individual checks and overall validity
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  const checks = {
    minLength: password.length >= 8,
    hasLetter: /[a-zA-Z]/.test(password),
    // Accept any number or common special character (very permissive)
    // eslint-disable-next-line no-useless-escape
    hasNumberOrSymbol: /[\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password),
  };

  const isValid = checks.minLength && checks.hasLetter && checks.hasNumberOrSymbol;

  return { checks, isValid };
};

/**
 * Helper text for password fields
 */
export const PASSWORD_HELPER_TEXT =
  '💡 Tip: Most passwords generated by 1Password, LastPass, or other password managers will work!';
